<script setup lang="ts">
import { computed } from 'vue'
import { formatNumberFullTruncate, numberToStringFormat } from '@/helpers/utils'
import { T } from '@tolgee/vue'
import BaseLinkButton from '@/components/base/BaseLinkButton.vue'
import { useModals } from '@/composables/modal-helper'

interface Props {
  direction?: 'top' | 'right' | 'bottom' | 'left'
  hover?: boolean
  formattingType?: 'none' | 'semi' | 'full'
  number: number | string | null
  disabled?: boolean

}

const props = withDefaults(defineProps<Props>(), {
  hover: false,
  direction: 'bottom',
  formattingType: 'full',
  disabled: false,
})

const { setShowNumberFormatModal } = useModals()

const openDelay = computed(() => (props.hover ? 500 : 0))

const usedNumber = computed(() => Number(props.number) ?? 0)

const disabled = computed(() => {
  switch (props.formattingType) {
  case 'none': {
    return true
  }
  case 'semi': {
    return usedNumber.value < 1e7
  }
  case 'full': {
    return usedNumber.value < 1e3
  }
  default: {
    return true
  }
  }
})

function onLearnMore() {
  setShowNumberFormatModal(true)
}
</script>
<template>
  <VTooltip
    :placement="direction"
    :hover="hover"
    :open-delay="openDelay"
    :disabled="disabled"
    arrow
    class="number-formatting-tooltip"
  >
    <slot />
    <template
      #popper
    >
      <div
        class="flex flex-col items-center gap-y-1"
      >
        <span class="text0base font-semibold unslashed-zero">{{ numberToStringFormat(usedNumber) }}</span>
        <span v-if="usedNumber >= 1e9 && usedNumber < 1e15">
          {{ formatNumberFullTruncate(number, false, true) }}
        </span>
        <span
          v-else-if="usedNumber >= 1e15"
          class="flex flex-col items-center"
        >
          <span>{{ formatNumberFullTruncate(usedNumber, false, true) }}</span>
          <!--          <span class="whitespace-pre-line">-->
          <!--            <T-->
          <!--              key-name="number_formatting.tooltip.thats"-->
          <!--              :default-value="`, that’s\n`"-->
          <!--            />-->
          <!--          </span>-->
          <!--          <span>{{ `${numberToStringFormat(usedNumber, 1e12)} Trillion` }}</span>-->
          <BaseLinkButton @click="onLearnMore">
            <T
              key-name="number_formatting.tooltip.learn_more"
              default-value="Learn more"
            />
          </BaseLinkButton>
        </span>
      </div>
    </template>
  </VTooltip>
</template>
<style>

.number-formatting-tooltip {
  display: inline;

  .v-popper--theme-tooltip {
    @apply !px-0.5;

    .v-popper__inner {
      @apply !bg-dark-top-layer !text-white !text-sm !px-3 !py-2 !rounded-lg !outline-0 !max-w-[260px] !text-center !z-[800];
      box-shadow: 0px 4px 4px 0px rgba(0, 0, 0, 0.25);
      font-family: "Space Grotesk Variable", sans-serif;
      -webkit-font-smoothing: antialiased;
      -moz-osx-font-smoothing: grayscale;
      font-feature-settings: 'ss03', 'ss04', 'zero' 1;

      div {
        @apply !font-medium !max-w-[240px] !whitespace-pre-wrap;
      }
    }

    .v-popper__arrow-outer {
      @apply !border-dark-top-layer;
    }

    .v-popper__arrow-inner {
      @apply !border-dark-top-layer;
    }

    .v-popper__arrow-container {
      z-index: -1 !important;
    }
  }
}

</style>
